// frontend/src/components/dashboard/AccommodationManager.jsx
import React, { useState, useEffect } from 'react';
import { PlusIcon, HomeIcon, CalendarIcon, CurrencyRupeeIcon, MapPinIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';

const AccommodationManager = ({ groupId, membership, user }) => {
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [newAccommodation, setNewAccommodation] = useState({
    name: '',
    type: 'hotel',
    location: '',
    checkIn: '',
    checkOut: '',
    cost: '',
    notes: ''
  });
  
  useEffect(() => {
    loadAccommodations();
  }, [groupId]);
  
  const loadAccommodations = async () => {
    try {
      setLoading(true);
      const accommodationsData = await groupDashboardService.getAccommodations(groupId);
      setAccommodations(accommodationsData);
    } catch (error) {
      console.error('Failed to load accommodations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddAccommodation = async (e) => {
    e.preventDefault();
    try {
      await groupDashboardService.addAccommodation(groupId, newAccommodation);
      
      setNewAccommodation({
        name: '',
        type: 'hotel',
        location: '',
        checkIn: '',
        checkOut: '',
        cost: '',
        notes: ''
      });
      setShowAddForm(false);
      loadAccommodations();
    } catch (error) {
      console.error('Failed to add accommodation:', error);
    }
  };
  
  const accommodationTypes = [
    { id: 'hotel', name: 'Hotel' },
    { id: 'hostel', name: 'Hostel' },
    { id: 'airbnb', name: 'Airbnb' },
    { id: 'homestay', name: 'Homestay' },
    { id: 'camping', name: 'Camping' },
    { id: 'resort', name: 'Resort' },
    { id: 'guesthouse', name: 'Guesthouse' },
    { id: 'other', name: 'Other' }
  ];
  
  const bookingStatusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    completed: 'bg-blue-100 text-blue-800'
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading accommodations...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Accommodation</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Accommodation
        </Button>
      </div>
      
      {/* Add Accommodation Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100">
          <h3 className="font-bold text-gray-800 mb-4 text-lg">Add Accommodation</h3>
          
          <form onSubmit={handleAddAccommodation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Accommodation Name
                </label>
                <input
                  type="text"
                  value={newAccommodation.name}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Sunset Resort"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={newAccommodation.type}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {accommodationTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={newAccommodation.location}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Beach Road, Goa"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost (₹)
                </label>
                <input
                  type="number"
                  value={newAccommodation.cost}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, cost: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-in Date
                </label>
                <input
                  type="date"
                  value={newAccommodation.checkIn}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, checkIn: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-out Date
                </label>
                <input
                  type="date"
                  value={newAccommodation.checkOut}
                  onChange={(e) => setNewAccommodation({ ...newAccommodation, checkOut: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={newAccommodation.notes}
                onChange={(e) => setNewAccommodation({ ...newAccommodation, notes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows="3"
                placeholder="Any special notes or instructions..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2 rounded-lg"
              >
                Add Accommodation
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {/* Accommodations List */}
      <div className="space-y-4">
        {accommodations.length === 0 ? (
          <div className="text-center py-12">
            <HomeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Accommodations Added</h3>
            <p className="text-gray-600 mb-6">Add your accommodation details to share with the group!</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg"
            >
              Add First Accommodation
            </Button>
          </div>
        ) : (
          accommodations.map((accommodation) => (
            <div key={accommodation._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center mb-2">
                    <HomeIcon className="h-5 w-5 text-indigo-500 mr-2" />
                    <span className="text-sm text-gray-600 capitalize">{accommodation.type}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">{accommodation.name}</h3>
                  <p className="text-gray-600 mt-1">{accommodation.location}</p>
                </div>
                
                <div className="flex flex-col items-end">
                  {accommodation.cost > 0 && (
                    <div className="text-right mb-2">
                      <div className="text-sm text-gray-500">Total Cost</div>
                      <div className="text-2xl font-bold text-indigo-600">₹{accommodation.cost}</div>
                    </div>
                  )}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${bookingStatusColors[accommodation.bookingStatus]}`}>
                    {accommodation.bookingStatus.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {accommodation.checkIn && (
                  <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-blue-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Check-in</p>
                      <p className="font-medium">{new Date(accommodation.checkIn).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
                
                {accommodation.checkOut && (
                  <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                    <CalendarIcon className="h-5 w-5 text-purple-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-600">Check-out</p>
                      <p className="font-medium">{new Date(accommodation.checkOut).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {accommodation.notes && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{accommodation.notes}</p>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-2">
                    {accommodation.createdBy?.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{accommodation.createdBy?.name}</p>
                    <p className="text-xs text-gray-500">
                      Added {new Date(accommodation.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500">
                  Shared with {accommodation.sharedBy?.length || 1} members
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AccommodationManager;